// ─── REFERRAL TRACKING UTILITIES ─────────────────────────────────────────────
// Handles capturing referral codes from URLs and managing affiliate referrals
import { db } from '../firebase';
import {
  doc, getDoc, setDoc, updateDoc, collection,
  query, where, getDocs, serverTimestamp, increment, arrayUnion
} from 'firebase/firestore';

// ─── COMMISSION CONSTANTS ────────────────────────────────────────────────────
// Default commission rates (fraction of plan price paid to affiliate per referral)
export const DEFAULT_COMMISSION_STRUCTURE = {
    coreMonthly: 0.50,
    proMonthly:  0.50,
    coreYearly:  0.50,
    proYearly:   0.50,
};


// Plan prices in USD — must stay in sync with src/config/plans.js
export const PLAN_PRICES = {
    coreMonthly: 6.99,
    proMonthly:  9.99,
    coreYearly:  69.90,
    proYearly:   99.90,
};

// Monthly revenue per subscriber (yearly plans divided by 12)
export const MONTHLY_REVENUE = {
  coreMonthly: 6.99,
  proMonthly:  9.99,
  coreYearly:  parseFloat((69.90  / 12).toFixed(2)),  // 5.83
  proYearly:   parseFloat((99.90 / 12).toFixed(2)),   // 8.33
};

const COMMISSION_RATE_DEFAULT = 0.50;

// ─── CALCULATE AFFILIATE EARNINGS (new threshold system) ─────────────────
// Commission is earned only on subscribers beyond those needed to cover operating cost.
export function calculateAffiliateEarnings(affiliateData, operatingCost = 40, leeway = 1) {
  const effectiveThreshold = operatingCost - leeway;
  const structure = affiliateData.commissionStructure || {};

  const subscribers = [];
  const counts = {
    coreMonthly: affiliateData.coreMonthlyCount || 0,
    proMonthly:  affiliateData.proMonthlyCount  || 0,
    coreYearly:  affiliateData.coreYearlyCount  || 0,
    proYearly:   affiliateData.proYearlyCount   || 0,
  };
  for (const [type, count] of Object.entries(counts)) {
    for (let i = 0; i < count; i++) {
      subscribers.push({ type, revenue: MONTHLY_REVENUE[type] });
    }
  }
  subscribers.sort((a, b) => b.revenue - a.revenue);

  let cumulativeRevenue = 0;
  let totalCommission = 0;
  let thresholdMet = false;
  let subscribersCoveringThreshold = 0;
  let subscribersEarningCommission = 0;

  for (const sub of subscribers) {
    cumulativeRevenue = parseFloat((cumulativeRevenue + sub.revenue).toFixed(2));
    if (!thresholdMet && cumulativeRevenue >= effectiveThreshold) {
      thresholdMet = true;
      subscribersCoveringThreshold++;
    } else if (thresholdMet) {
      const rate = structure[sub.type] ?? COMMISSION_RATE_DEFAULT;
      totalCommission = parseFloat(
        (totalCommission + parseFloat((sub.revenue * rate).toFixed(2))).toFixed(2)
      );
      subscribersEarningCommission++;
    } else {
      subscribersCoveringThreshold++;
    }
  }

  return {
    thresholdMet,
    totalMonthlyRevenue: parseFloat(cumulativeRevenue.toFixed(2)),
    effectiveThreshold,
    subscribersCoveringThreshold,
    subscribersEarningCommission,
    totalMonthlyCommission: parseFloat(totalCommission.toFixed(2)),
    subscribers,
  };
}

// ─── CAPTURE REFERRAL CODE FROM URL ─────────────────────────────────────────
// Call this on app load — checks for ?ref=CODE in the URL
export function captureReferralCode() {
  const params = new URLSearchParams(window.location.search);
  const refCode = params.get('ref');
  if (refCode) {
    const upper = refCode.toUpperCase().trim();
    const ts = new Date().toISOString();
    // localStorage: 30-day persistence (survives browser close)
    localStorage.setItem('tracksy_ref', upper);
    localStorage.setItem('tracksy_ref_ts', ts);
    // sessionStorage: backup in case localStorage is cleared mid-OAuth
    sessionStorage.setItem('tracksy_ref', upper);
    sessionStorage.setItem('tracksy_ref_ts', ts);
    // Clean URL without reload
    const url = new URL(window.location);
    url.searchParams.delete('ref');
    window.history.replaceState({}, '', url.toString());
  }
  return localStorage.getItem('tracksy_ref') || sessionStorage.getItem('tracksy_ref') || null;
}

// ─── GET STORED REFERRAL CODE ───────────────────────────────────────────────
export function getStoredReferralCode() {
  // Try localStorage first (30-day persistence), then sessionStorage (session backup)
  const code = localStorage.getItem('tracksy_ref') || sessionStorage.getItem('tracksy_ref');
  const ts = localStorage.getItem('tracksy_ref_ts') || sessionStorage.getItem('tracksy_ref_ts');
  if (!code || !ts) return null;
  const daysSince = (Date.now() - new Date(ts).getTime()) / (1000 * 60 * 60 * 24);
  if (daysSince > 30) {
    localStorage.removeItem('tracksy_ref');
    localStorage.removeItem('tracksy_ref_ts');
    sessionStorage.removeItem('tracksy_ref');
    sessionStorage.removeItem('tracksy_ref_ts');
    return null;
  }
  return code;
}

// ─── CLEAR REFERRAL CODE (after successful signup) ──────────────────────────
export function clearReferralCode() {
  localStorage.removeItem('tracksy_ref');
  localStorage.removeItem('tracksy_ref_ts');
  sessionStorage.removeItem('tracksy_ref');
  sessionStorage.removeItem('tracksy_ref_ts');
}

// ─── VALIDATE AFFILIATE CODE EXISTS IN FIREBASE ───────────────────────────────
// Uses the /api/validate-referral server-side endpoint (Admin SDK) so Firestore
// security rules on the affiliates collection don't block the lookup.
export async function validateAffiliateCode(code) {
  try {
    const normalised = code.trim().toUpperCase();
    const res = await fetch('/api/validate-referral', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: normalised }),
    });

    if (!res.ok) {
      console.error('[referral] validateAffiliateCode: server error', res.status);
      return null;
    }

    const data = await res.json();

    if (!data.valid) {
      console.log('[referral] validateAffiliateCode: no affiliate found for code:', normalised);
      return null;
    }

    console.log('[referral] validateAffiliateCode: found affiliate', data.affiliateId);
    return { id: data.affiliateId, referralCode: data.referralCode };
  } catch (err) {
    console.error('[referral] validateAffiliateCode error:', err);
    return null;
  }
}

// ─── RECORD A REFERRAL ON SIGNUP ────────────────────────────────────────────
// Called during user registration when a referral code is present
export async function recordReferral(newUserId, userEmail, affiliateCode) {
  try {
    // Check if a referral already exists for this user.
    // .catch({ empty: true }) — if Firestore rules block the read, treat as
    // "no duplicate found" rather than aborting the whole referral submission.
    const byId = await getDocs(query(
      collection(db, 'referrals'), where('userId', '==', newUserId)
    )).catch(() => ({ empty: true }));
    if (!byId.empty) return false;

    const byEmail = await getDocs(query(
      collection(db, 'referrals'), where('userEmail', '==', userEmail)
    )).catch(() => ({ empty: true }));
    if (!byEmail.empty) return false;

    const affiliate = await validateAffiliateCode(affiliateCode);
    if (!affiliate) return false;

    // Create referral record
    const referralRef = doc(collection(db, 'referrals'));
    await setDoc(referralRef, {
      userId: newUserId,
      userEmail: userEmail,
      affiliateId: affiliate.id,
      affiliateCode: affiliateCode.toUpperCase(),
      referralCode: affiliateCode.toUpperCase(),   // same value, canonical name
      signupDate: serverTimestamp(),
      status: 'trial',          // trial → active → churned → cancelled
      plan: '',                 // filled in when subscriber activates (updateReferralStatus)
      billingCycle: 'monthly',  // default; updated on activation
      subscriptionStart: null,
      subscriptionPlan: null,
      monthlyRate: 0,
      totalCommissionPaid: 0,
      lastPaymentDate: null,
      createdAt: serverTimestamp()
    });

    // Update affiliate stats — non-fatal; webhook handles plan counters.
    // If client-side rules block this write it logs but doesn't fail the referral.
    await updateDoc(doc(db, 'affiliates', affiliate.id), {
      totalReferrals: increment(1),
      lastReferralDate: serverTimestamp(),
      referredUsers: arrayUnion(newUserId),
    }).catch(err => console.warn('[referral] affiliate stat update failed (non-fatal):', err.message));

    clearReferralCode();
    return true;
  } catch (err) {
    console.error('[referral] recordReferral error:', err);
    return false;
  }
}

// ─── UPDATE REFERRAL STATUS (when user subscribes/cancels) ──────────────────
// Commission only applies to referred subscribers — never organic signups.
// Currently dead code — call sites will be wired in the Stripe webhook phase.
export async function updateReferralStatus(userId, status, plan, billingCycle = 'monthly') {
  try {
    const q = query(collection(db, 'referrals'), where('userId', '==', userId));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return;

    const referralDoc = snapshot.docs[0];
    const referralData = referralDoc.data();
    const normalizedPlan = (plan || referralData.plan || '').toLowerCase();
    // Default billingCycle to 'monthly' — legacy docs predate this field
    const normalizedCycle = (billingCycle || referralData.billingCycle || 'monthly').toLowerCase();

    const updates = { status };
    if (plan) {
      updates.plan = normalizedPlan;
      updates.subscriptionPlan = plan;
    }
    if (billingCycle) updates.billingCycle = normalizedCycle;
    if (status === 'active' && !referralData.subscriptionStart) {
      updates.subscriptionStart = serverTimestamp();
    }
    await updateDoc(referralDoc.ref, updates);

    // Determine which counter to adjust
    const affiliateId = referralData.affiliateId;
    if (!affiliateId) return;

    const counterField = getCounterField(normalizedPlan, normalizedCycle);
    if (!counterField) return;

    if (status === 'active') {
      // Commission only applies to referred subscribers — never organic signups
      // Read affiliate doc first so calculateAffiliateCommission can check threshold
      const affiliateDocForCalc = await getDoc(doc(db, 'affiliates', affiliateId));
      if (affiliateDocForCalc.exists()) {
        // calculateAffiliateCommission uses count+1 internally — call BEFORE incrementing
        calculateAffiliateCommission(affiliateDocForCalc.data(), {
          affiliateCode: referralData.affiliateCode,
          plan: normalizedPlan,
          billingCycle: normalizedCycle,
        });
        // result logged/used by Stripe webhook phase; counter always increments for active referred subs
      }
      await updateDoc(doc(db, 'affiliates', affiliateId), {
        [counterField]: increment(1),
      });
    } else if (status === 'churned' || status === 'cancelled') {
      // Decrement counter — guard against going below 0
      const affiliateSnap = await getDoc(doc(db, 'affiliates', affiliateId));
      if (affiliateSnap.exists()) {
        const currentCount = affiliateSnap.data()[counterField] || 0;
        if (currentCount > 0) {
          await updateDoc(doc(db, 'affiliates', affiliateId), {
            [counterField]: increment(-1),
          });
        }
      }
    }
  } catch (err) {
    // Fail silently — collection may not exist yet
  }
}

// Maps plan + billingCycle to the affiliate doc counter field name
function getCounterField(plan, billingCycle) {
  if (plan === 'core' && billingCycle === 'monthly') return 'coreMonthlyCount';
  if (plan === 'pro'  && billingCycle === 'monthly') return 'proMonthlyCount';
  if (plan === 'core' && billingCycle === 'yearly')  return 'coreYearlyCount';
  if (plan === 'pro'  && billingCycle === 'yearly')  return 'proYearlyCount';
  return null;
}

// ─── GET ALL REFERRALS FOR AN AFFILIATE ────────────────────────────────────────
export async function getAffiliateReferrals(affiliateId) {
  try {
    const q = query(
      collection(db, 'referrals'),
      where('affiliateId', '==', affiliateId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // Collection may not exist yet — fail silently
    return [];
  }
}

// ─── GET AFFILIATE PROFILE BY USER ID ─────────────────────────────────────────
export async function getAffiliateByUserId(userId) {
  try {
    const affiliateDoc = await getDoc(doc(db, 'affiliates', userId));
    if (affiliateDoc.exists()) return { id: affiliateDoc.id, ...affiliateDoc.data() };
    return null;
  } catch (err) {
    // Collection may not exist yet — fail silently
    return null;
  }
}

// ─── CREATE / UPDATE AFFILIATE PROFILE (Admin function) ───────────────────────
export async function createAffiliate(userId, affiliateData) {
  try {
    const code = affiliateData.referralCode || generateReferralCode(affiliateData.name);
    // Explicitly list all fields — no spread to prevent old fields leaking in
    await setDoc(doc(db, 'affiliates', userId), {
      name: affiliateData.name || '',
      email: affiliateData.email || '',
      phone: affiliateData.phone || '',
      referralCode: code.toUpperCase(),
      // Commission counters — 4 separate tracked categories
      coreMonthlyCount: 0,
      proMonthlyCount: 0,
      coreYearlyCount: 0,
      proYearlyCount: 0,
      // Commission structure: rate as decimal (0.50 = 50%)
      commissionStructure: { ...DEFAULT_COMMISSION_STRUCTURE },
      // Payout tracking
      totalReferrals: 0,
      totalEarnings: 0,
      totalPaid: 0,
      pendingPayout: 0,
      isActive: true,
      createdAt: serverTimestamp(),
    });
    return code.toUpperCase();
  } catch (err) {
    return null;
  }
}

// ─── GENERATE UNIQUE REFERRAL CODE ──────────────────────────────────────────
function generateReferralCode(name) {
  const prefix = name ? name.replace(/[^a-zA-Z]/g, '').substring(0, 4).toUpperCase() : 'REF';
  const suffix = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}${suffix}`;
}

// ─── CALCULATE COMMISSION FOR A NEW REFERRED SUBSCRIBER ──────────────────────
// Called from updateReferralStatus BEFORE incrementing the counter.
// Uses count + 1 to simulate post-increment, so the caller increments after.
// Commission only applies to referred subscribers — never organic signups.
// Reads rate from affiliate.commissionStructure; falls back to DEFAULT_COMMISSION_STRUCTURE.
export function calculateAffiliateCommission(affiliate, subscription) {
  // Commission only applies to referred subscribers — never organic signups
  if (!subscription.affiliateCode || subscription.affiliateCode !== affiliate.referralCode) return 0;

  const plan = (subscription.plan || '').toLowerCase();
  const billingCycle = (subscription.billingCycle || 'monthly').toLowerCase();
  const key = `${plan}${billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}`;

  const structure = affiliate.commissionStructure || DEFAULT_COMMISSION_STRUCTURE;
  // Threshold logic moved to calculateAffiliateEarnings (revenue-based system)
  const rate  = structure[key] ?? DEFAULT_COMMISSION_STRUCTURE[key] ?? 0.50;
  const price = PLAN_PRICES[key] ?? 0;
  return parseFloat((price * rate).toFixed(2));
}

// ─── ONE-TIME MIGRATION: add commissionStructure + thresholds ────────────────
// Safe to call multiple times — only patches docs that are missing both fields.
// Call from AdminAffiliatePanel on load. Silently skips if already migrated.
export async function migrateAffiliateCommissionStructure() {
  try {
    const snapshot = await getDocs(collection(db, 'affiliates'));
    const migrations = [];
    for (const affiliateDoc of snapshot.docs) {
      const data = affiliateDoc.data();
      if (!data.commissionStructure) {
        const patch = {};
        if (!data.commissionStructure) patch.commissionStructure = { ...DEFAULT_COMMISSION_STRUCTURE };
        migrations.push(updateDoc(affiliateDoc.ref, patch));
      }
    }
    if (migrations.length > 0) {
      await Promise.all(migrations);
      console.log(`[referral] migrated ${migrations.length} affiliate(s) with default commissionStructure/thresholds`);
    }
    return migrations.length;
  } catch (err) {
    console.error('[referral] migrateAffiliateCommissionStructure error:', err);
    return 0;
  }
}

// ─── CANONICAL REFERRAL LINK ─────────────────────────────────────────────────
// Base: app.tracksyhq.com — both tracksyhq.com and app.tracksyhq.com serve the
// same React app via Vercel, but app.tracksyhq.com is the canonical app URL.
export function buildReferralLink(code) {
  return `https://app.tracksyhq.com?ref=${code}`;
}
